import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { Checkbox, IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Delete } from '@mui/icons-material';
import { gridDateTimeFormatter, withQueryParams } from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { IReminders } from '@shared/components/Layout/components/Header/components/popovers/RemindersPreview';
import { Wrapper } from '@styles/CommonStyles';
import { useTabs } from '@shared/hooks/useTabs';
import { withOpenTab } from '@shared/HOCs/withOpenTab';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { requestDeleteReminders } from '../requests';

const RemindersPage = () => {
  const { userId } = useUserAccount();
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const [loading, setLoading] = useState(false);

  const { data: reminders, mutate } = useSWR(userId && `/${SERVICES.NOTIFICATION}/reminders/${userId}`);

  const handleDeleteReminder = useCallback(
    async (reminderId: string, e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setLoading(true);
      await requestDeleteReminders(reminderId);
      mutate();
      setLoading(false);
    },
    [mutate]
  );

  const columns: GridColDef[] = [
    dataGridCol('reminderDate', {
      type: 'dateTime',
      valueFormatter: gridDateTimeFormatter,
    }),
    dataGridCol('contactNumericId', { headerName: 'CID' }),
    dataGridCol('contactFirstName', {
      headerName: 'First Name',
      width: 300,
    }),
    dataGridCol('contactLastName', {
      headerName: 'Last name',
      width: 300,
    }),
    dataGridCol('description', { headerName: 'Note' }),
    dataGridCol('actioned', {
      renderCell: (params) => (
        <Checkbox
          disabled
          checked={!!params.row?.actionDate}
        />
      ),
    }),
    dataGridCol('actionDate', {
      headerName: 'Actioned On',
      width: 200,
      type: 'dateTime',
      valueFormatter: gridDateTimeFormatter,
    }),
    dataGridCol('actions', {
      minWidth: 100,
      renderCell: ({ row }) => (
        <IconButton
          data-action="delete-row"
          title="Delete"
          onClick={(e) => handleDeleteReminder(row.id, e)}
        >
          <Delete
            sx={{
              width: '1.8rem',
              height: '1.8rem',
            }}
          />
        </IconButton>
      ),
    }),
  ].map((column) => ({ flex: 1, ...column }));

  const onReminderClick = useCallback((row: IReminders) => {
    const route = withQueryParams('/contact-manager', { id: row.contactId, tab: 0 }, false);
    const title = `${row.contactFirstName} ${row.contactLastName}`;
    openTab({ title, route });
    navigate(route);
  }, []);

  return (
    <Wrapper>
      <LoaderBackdrop open={loading} />
      <PhoenixGrid
        autoHeight
        withHeader
        noButton
        strict
        disableSelectionOnClick
        showClearAllFilter
        name="Reminders"
        columns={columns}
        rows={reminders || []}
        onRowClick={({ row }: { row: IReminders }) => onReminderClick(row)}
      />
    </Wrapper>
  );
};

export const Reminders = withOpenTab(RemindersPage, 'Reminders');